import { api } from './client';
import { FailureFilters, DEFAULT_FAILURE_FILTERS, OnyxDataFilter, DEFAULT_ONYX_DATA_FILTERS } from '@/types';
import { FailureFilter } from '@/types/failure';
import { transformFiltersToFailureFilter } from '@/utils/dataTransform';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { CensoredDataPoint } from '@/utils/weibullCensored';
import type { AnalysisMethod } from '@/hooks/useWeibullAnalysis';
import { VblAnalysisResult, TurbineToForecast } from '@/types/vbl';
import { ForecastConfig, ForecastResult, SimulationMetadata } from '@/types/forecast';
import { MLEResult } from '@/utils/weibullMLE';
import type { TurbineWorkspaceMapping } from '@/types/turbineWorkspaceMapping';
import type { VblForecastResult, VblForecastConfiguration } from '@/types/vblForecast';

/**
 * Server-computed Weibull analysis metadata. Mirrors the inline `metadata`
 * object the hook previously derived client-side. Non-finite ratios (e.g.
 * onyxCensoringPercentage when there is no ONYX data) come back as JSON null.
 */
export interface WeibullAnalysisMetadata {
  totalSamples: number;
  totalFailures: number;
  totalCensor: number;
  totalCustomerDataCount: number;
  customerFailureCount: number;
  customerCensorCount: number;
  onyxDataCount: number;
  onyxFailureCount: number;
  onyxCensorCount: number;
  censoringPercentage: number | null;
  customerCensoringPercentage: number | null;
  onyxCensoringPercentage: number | null;
  hasCensoredData: boolean;
  hasONYXData: boolean;
}

export interface WeibullDiversityMetrics {
  uniqueSites: number;
  uniqueTurbines: number;
  uniqueComponents: number;
}

export interface MethodRecommendation {
  method: AnalysisMethod;
  reason: string;
  confidence: 'low' | 'medium' | 'high';
}

/**
 * Supporting plot data for the censored/onyx-censored overlays on the
 * probability plot. Shapes match `CensoredDataPoint` so the chart components
 * (DualPlotView) consume it unchanged.
 */
export interface WeibullSupportingPlotData {
  censorPlot: CensoredDataPoint[];
  onyxCensorPlot: CensoredDataPoint[];
}

/**
 * Full envelope returned by POST /api/v2/analysis/weibull. The keys map 1:1 to
 * the public return contract of useWeibullAnalysis.
 */
export interface WeibullAnalysisResponse {
  analysis: WeibullAnalysisResult | null;
  metadata: WeibullAnalysisMetadata | null;
  diversityMetrics: WeibullDiversityMetrics;
  selectedMethod: AnalysisMethod;
  recommendation: MethodRecommendation | null;
  hasEnoughData: boolean;
  sampleSize: number;
  supportingPlotData: WeibullSupportingPlotData;
}

/**
 * Onyx filter shape on the request body (camelCase, matching the embedded
 * FailureFilterV2.onyx_filters the backend expects).
 */
interface RequestOnyxFilters {
  componentsMajor: string[];
  componentsMinor: string[];
  turbineModels: string[];
}

/**
 * The UUID-based FailureFilterV2 carried in the request body. Extends the
 * shared FailureFilter (name->UUID mapped fields) with the onyx-data flags.
 */
interface RequestFailureFilter extends FailureFilter {
  fetch_onyx_data: boolean;
  onyx_filters: RequestOnyxFilters;
}

export interface WeibullAnalysisRequest {
  filter: RequestFailureFilter;
  confidence_level: number;
  method: AnalysisMethod | null;
  use_onyx_data: boolean;
  use_onyx_data_filter: boolean;
  operation_time_min: number | null;
  operation_time_max: number | null;
  date_from: string | null;
  date_to: string | null;
}

/**
 * Coerce a possibly-undefined number range bound to the explicit `null` the
 * backend expects when the bound is not set.
 */
const toNullableNumber = (value: number | null | undefined): number | null =>
  value === undefined || value === null || Number.isNaN(value) ? null : value;

const toNullableString = (value: string | undefined): string | null =>
  value === undefined || value === null || value === '' ? null : value;

/**
 * Server-computed VBL analysis metadata. Mirrors the inline `metadata` object
 * the VBL hook previously derived client-side. VBL data is never censored, so
 * `censoredCount` is always 0 and `hasCensoredData` is always false.
 */
export interface VblAnalysisMetadata {
  totalSamples: number;
  failureCount: number;
  censoredCount: number;
  hasCensoredData: boolean;
  degradationTimeRange: { min: number; max: number };
  sites: string[];
  components: string[];
}

export interface VblDiversityMetrics {
  uniqueSites: number;
  uniqueTurbines: number;
  uniqueComponents: number;
  uniqueFailureModes: number;
}

/**
 * Full envelope returned by POST /api/v2/analysis/vbl. The keys map 1:1 to the
 * public return contract of useVblAnalysis. `analysis` is null when there are
 * fewer than 2 valid records (hasEnoughData=false). `metadata` and
 * `diversityMetrics` are always populated (possibly zero-count) objects — the
 * server defaults them to `new()` and never emits null — so they are
 * non-nullable here (mirroring WeibullAnalysisResponse.metadata in Phase 1B).
 */
export interface VblAnalysisResponse {
  analysis: VblAnalysisResult | null;
  metadata: VblAnalysisMetadata;
  diversityMetrics: VblDiversityMetrics;
  hasEnoughData: boolean;
  sampleSize: number;
}

/**
 * Request body for POST /api/v2/analysis/vbl. `filter` is the UUID-based
 * VblFilter (identical shape to FailureFilter — name->UUID mapped, with
 * `case_id` carrying the EXCLUDED case-ids). The client-only range/date filters
 * are forwarded in the body (applied server-side to degradation_time in DAYS /
 * down_date), so they must be part of the cache key.
 */
export interface VblAnalysisRequest {
  filter: FailureFilter;
  confidence_level: number;
  operation_time_min: number | null;
  operation_time_max: number | null;
  date_from: string | null;
  date_to: string | null;
}

/**
 * Full envelope returned by POST /api/v2/analysis/forecast/weibull (Phase 3B).
 * The keys of `results`/`metadata` already match the camelCase TS
 * `ForecastResult`/`SimulationMetadata` shapes, so they are reused verbatim —
 * the server maps 1:1 to the worker's old postback. `simulationData` is
 * deliberately NOT on the wire (the worker kept it in-memory only).
 */
export interface WeibullForecastResponse {
  results: ForecastResult[];
  metadata: SimulationMetadata;
}

/**
 * Request body for POST /api/v2/analysis/forecast/weibull (snake_case).
 *
 * Mirrors the worker payload, split into:
 * - the analysis inputs (filter + analysis params) the server would use to
 *   re-fit the Weibull, and
 * - the explicit fit override (`shape`/`scale`/`mle_result`) that short-circuits
 *   the re-fit so the forecast uses the EXACT fit the user is viewing, plus
 * - the forecast configuration.
 *
 * `forecast_confidence_level` is SEPARATE from the analysis `confidence_level`:
 * it comes from `ForecastConfig.confidenceLevel` (the worker's
 * `confidenceOptions.confidenceLevel`).
 */
export interface WeibullForecastRequest {
  filter: FailureFilter;
  confidence_level: number;
  method: AnalysisMethod | null;
  use_onyx_data: boolean;
  use_onyx_data_filter: boolean;
  operation_time_min: number | null;
  operation_time_max: number | null;
  date_from: string | null;
  date_to: string | null;

  horizon_years: number;
  granularity_months: number;
  start_date: string | null;
  use_monte_carlo_simulation: boolean;
  forecast_confidence_level: number;
  include_confidence_intervals: boolean;
  include_planned_failures: boolean;
  parameter_samples: number;
  trajectories_per_parameter: number;
  seed: number | null;

  // Explicit fit override (Phase 3B `ResolveFit` short-circuits the re-fit when
  // shape+scale are present). `mle_result` carries the parameter uncertainty so
  // the parametric-bootstrap path reproduces the analysis the user is viewing.
  shape?: number;
  scale?: number;
  mle_result?: MLEResult | null;
}

/**
 * The per-item VBL scope the server-side forecast endpoint fits the Johnson
 * Weibull from (Phase 4B'). It is the UUID-based `VblFilter` (identical shape to
 * the analysis `FailureFilter`): the mapping's component UUID(s) + the turbine's
 * site UUID. The server fetches the VBL records for this scope, runs the Johnson
 * fit itself, and drops the turbine if there are < 2 valid records or the fit is
 * null/degenerate. This replaces the precomputed client-side `fit`.
 */
export interface VblForecastFilterRequest {
  major_component_id: string[];
  component_id: string[];
  site_id: string[];
}

/**
 * A single batch item for POST /api/v2/analysis/forecast/vbl (Phase 4B'). `turbine`
 * and `workspace_mapping` are pure snake_case echoes (the TS shapes are already
 * snake_case). `filter` scopes the SERVER-SIDE Johnson fit (the fit is no longer
 * computed client-side); `confidence_level` is the Johnson-fit CI level. Only
 * MAPPED turbines are sent; unmapped turbines are handled client-side (Phase 4C')
 * and never reach the wire. The server may still DROP a sent turbine whose fit
 * has < 2 valid records or is degenerate — only surviving forecasts come back.
 */
export interface VblForecastItemRequest {
  turbine: TurbineToForecast;
  current_degradation_time: number;
  has_mapping: boolean;
  workspace_mapping: TurbineWorkspaceMapping | null;
  last_inspection_severity: string | null;
  filter: VblForecastFilterRequest;
  confidence_level: number;
}

/**
 * Forecast configuration on the wire (snake_case). Mirrors the
 * `VblForecastConfiguration` the worker/calculator consumed. NB
 * `covariance_reduction_factor` is deliberately sent as `null` — the hook
 * historically never set it, and the server treats absent/null as the faithful
 * production value 1.0. Sending 0.7 (the engine default) would DIVERGE from the
 * legacy client behaviour, so we never do.
 */
export interface VblForecastConfigRequest {
  horizon_days: number;
  interval_days: number;
  start_date: string;
  confidence_level: number;
  bootstrap_samples: number;
  sampling_method: string | null;
  rejection_sample_limit: number | null;
  use_log_normal_scale: boolean | null;
  use_survival_adaptation: boolean | null;
  survival_adaptation_strength: number | null;
  early_stage_threshold: number | null;
  use_point_estimates: boolean | null;
  covariance_reduction_factor: number | null;
  include_confidence_intervals: boolean;
}

export interface VblForecastRequestBody {
  items: VblForecastItemRequest[];
  config: VblForecastConfigRequest;
  seed: number | null;
}

/**
 * Envelope returned by POST /api/v2/analysis/forecast/vbl (Phase 4B). Each
 * `VblForecastResult` is already in the camelCase TS shape — the server did the
 * date/risk/probability assembly — so the type is reused verbatim.
 */
export interface VblForecastResponse {
  results: VblForecastResult[];
}

/**
 * Analysis API service. Wraps the server-side Weibull computation endpoint
 * introduced in Phase 1B so the React hook becomes a thin fetch.
 */
class AnalysisApiService {
  /**
   * Run server-side Weibull (Failures) analysis.
   *
   * Builds the UUID-based FailureFilterV2 from the frontend name-based filters
   * using the SAME name->UUID mapping the legacy analysis path used, attaches
   * the onyx-data flags, forwards the client-only range/date filters in the
   * body (previously applied client-side), and returns the full server-computed
   * envelope mapped 1:1 to the hook contract.
   */
  async getWeibullAnalysis(
    filters: FailureFilters = DEFAULT_FAILURE_FILTERS,
    confidenceLevel: number,
    method: AnalysisMethod | undefined,
    useONYXData: boolean,
    useONYXDataFilter: boolean,
    onyxFilters: OnyxDataFilter = DEFAULT_ONYX_DATA_FILTERS,
  ): Promise<WeibullAnalysisResponse> {
    // Map display names -> UUIDs (sites/components/major-components/outcomes/
    // models) exactly as the failures analysis call does. case_id carries the
    // EXCLUDED case-ids (the backend negates this list).
    const mappedFilter = await transformFiltersToFailureFilter(filters);

    const filter: RequestFailureFilter = {
      ...mappedFilter,
      fetch_onyx_data: useONYXData,
      onyx_filters: {
        componentsMajor: onyxFilters.componentsMajor ?? [],
        componentsMinor: onyxFilters.componentsMinor ?? [],
        turbineModels: onyxFilters.turbineModels ?? [],
      },
    };

    const body: WeibullAnalysisRequest = {
      filter,
      confidence_level: confidenceLevel,
      method: method ?? null,
      use_onyx_data: useONYXData,
      use_onyx_data_filter: useONYXDataFilter,
      operation_time_min: toNullableNumber(filters.operationTimeMin),
      operation_time_max: toNullableNumber(filters.operationTimeMax),
      date_from: toNullableString(filters.dateFrom),
      date_to: toNullableString(filters.dateTo),
    };

    return api.post<WeibullAnalysisResponse>('/analysis/weibull', body);
  }

  /**
   * Run server-side VBL (Vibration-Based Life) analysis.
   *
   * Builds the UUID-based VblFilter from the frontend name-based filters using
   * the SAME name->UUID mapping the legacy VBL analysis path used (`case_id`
   * carries the EXCLUDED case-ids, which the backend negates), forwards the
   * client-only range/date filters in the body (previously applied
   * client-side: numeric bounds on degradation_time in DAYS, date bounds on
   * down_date), and returns the full server-computed envelope mapped 1:1 to the
   * hook contract.
   */
  async getVblAnalysis(
    filters: FailureFilters = DEFAULT_FAILURE_FILTERS,
    confidenceLevel: number,
  ): Promise<VblAnalysisResponse> {
    const filter = await transformFiltersToFailureFilter(filters);

    const body: VblAnalysisRequest = {
      filter,
      confidence_level: confidenceLevel,
      operation_time_min: toNullableNumber(filters.operationTimeMin),
      operation_time_max: toNullableNumber(filters.operationTimeMax),
      date_from: toNullableString(filters.dateFrom),
      date_to: toNullableString(filters.dateTo),
    };

    return api.post<VblAnalysisResponse>('/analysis/vbl', body);
  }

  /**
   * Run server-side Weibull failure forecast (Phase 3B endpoint
   * POST /api/v2/analysis/forecast/weibull).
   *
   * Replaces the client-side web-worker forecast. The server fetches the
   * failures + turbine population from `filter` (name->UUID mapped, `case_id`
   * carrying EXCLUDED ids — identical to the analysis path), and we OVERRIDE the
   * fit with the EXACT parameters the user is viewing by sending
   * `shape`/`scale`/`mle_result` from `sourceAnalysis`. This guarantees the
   * forecast uses the displayed fit rather than a fresh server re-fit (which
   * could differ).
   *
   * `seed` is supplied by the caller (the hook generates a fresh value on each
   * explicit "Calculate") to preserve the per-run behaviour of the worker —
   * each run produced a fresh Monte-Carlo seed. It is part of the caller's cache
   * key so the same seed yields the same (cached) result and a new seed forces a
   * recompute. Defaults to `Date.now()` for non-hook callers.
   * `forecast_confidence_level` comes from `configuration.confidenceLevel`. The
   * analysis-only params (method/use_onyx_data/range/date) are defaulted here
   * because the fit is overridden — the server's `ResolveFit` short-circuits on
   * shape+scale and never consults them — but the filter still scopes the
   * population to the user's site/model/component selection.
   */
  async getWeibullForecast(
    sourceAnalysis: WeibullAnalysisResult,
    filters: FailureFilters = DEFAULT_FAILURE_FILTERS,
    configuration: ForecastConfig,
    seed: number = Date.now(),
  ): Promise<WeibullForecastResponse> {
    const filter = await transformFiltersToFailureFilter(filters);

    const body: WeibullForecastRequest = {
      filter,
      // Analysis inputs: defaulted because the explicit fit override below
      // short-circuits the server re-fit. They are still sent (not omitted) so
      // the wire contract is complete and unambiguous.
      confidence_level: configuration.confidenceLevel,
      method: null,
      use_onyx_data: false,
      use_onyx_data_filter: false,
      operation_time_min: toNullableNumber(filters.operationTimeMin),
      operation_time_max: toNullableNumber(filters.operationTimeMax),
      date_from: toNullableString(filters.dateFrom),
      date_to: toNullableString(filters.dateTo),

      // Forecast configuration (mirrors the worker payload).
      horizon_years: configuration.horizonYears,
      granularity_months: configuration.granularityMonths,
      start_date: toNullableString(configuration.startDate),
      use_monte_carlo_simulation: configuration.useMonteCarloSimulation,
      forecast_confidence_level: configuration.confidenceLevel,
      include_confidence_intervals: configuration.includeConfidenceIntervals,
      include_planned_failures: configuration.includePlannedFailures ?? true,
      // Worker defaults: parameterSamples || 30, trajectoriesPerParameter || 50.
      parameter_samples: configuration.parameterSamples ?? 30,
      trajectories_per_parameter: configuration.trajectoriesPerParameter ?? 50,
      seed,

      // Explicit fit override — the EXACT fit the user is viewing.
      shape: sourceAnalysis.parameters.shape,
      scale: sourceAnalysis.parameters.scale,
      mle_result: sourceAnalysis.mleResult ?? null,
    };

    return api.post<WeibullForecastResponse>('/analysis/forecast/weibull', body);
  }

  /**
   * Run the server-side per-turbine Monte-Carlo VBL forecast in a single batch
   * (Phase 4B' endpoint POST /api/v2/analysis/forecast/vbl).
   *
   * Replaces the client-side `calculateMonteCarloVBL` loop. The turbine ->
   * workspace RESOLUTION stays on the frontend (the hook resolves the mapping
   * per turbine and supplies the per-item `filter` + `confidence_level`); the
   * SERVER fetches the VBL records for `filter`, fits the Johnson Weibull, and
   * runs the Monte-Carlo engine + date/risk/probability assembly per item. A
   * turbine whose server-side fit has < 2 valid records or is degenerate is
   * DROPPED (no result emitted), so `results` carries only surviving forecasts.
   *
   * The frontend `VblForecastConfiguration` is mapped to the snake_case wire
   * config here. `covariance_reduction_factor` is sent as `null` (NOT 0.7): the
   * legacy calculator never set it and the server treats null as 1.0, so this
   * preserves the exact current behaviour. `use_point_estimates` is likewise
   * absent from the calculator's options, so it is sent `null` (server default
   * false).
   *
   * `seed` is supplied per run by the caller (the hook sends `Date.now()` on
   * each Calculate) to preserve the worker/engine's per-run non-determinism: the
   * old engine seeded itself afresh each run. The server derives a distinct
   * stream per item (`seed + i`) so turbines stay independent yet the batch is
   * reproducible for a given seed. Defaults to `Date.now()` for non-hook callers.
   */
  async getVblForecast(
    items: VblForecastItemRequest[],
    configuration: VblForecastConfiguration,
    seed: number = Date.now(),
  ): Promise<VblForecastResponse> {
    const config: VblForecastConfigRequest = {
      horizon_days: configuration.horizonDays,
      interval_days: configuration.intervalDays,
      start_date: configuration.startDate,
      confidence_level: configuration.confidenceLevel,
      bootstrap_samples: configuration.bootstrapSamples,
      // Optional Monte-Carlo knobs: forwarded as-is (null when unset) so the
      // server applies the SAME `||`/`??` truthiness defaults the calculator did.
      sampling_method: configuration.samplingMethod ?? null,
      rejection_sample_limit: toNullableNumber(configuration.rejectionSampleLimit),
      use_log_normal_scale: configuration.useLogNormalScale ?? null,
      use_survival_adaptation: configuration.useSurvivalAdaptation ?? null,
      survival_adaptation_strength: toNullableNumber(configuration.survivalAdaptationStrength),
      early_stage_threshold: toNullableNumber(configuration.earlyStageThreshold),
      // The calculator never exposed point-estimate mode; keep server default.
      use_point_estimates: null,
      // Deliberately null — see method doc. Sending 0.7 would diverge.
      covariance_reduction_factor: null,
      include_confidence_intervals: configuration.includeConfidenceIntervals,
    };

    const body: VblForecastRequestBody = { items, config, seed };

    return api.post<VblForecastResponse>('/analysis/forecast/vbl', body);
  }
}

// Export singleton
export const analysisApi = new AnalysisApiService();
